/* eslint-disable no-secrets/no-secrets */
/**
 * Value Matching — React Router route tree.
 *
 * This file wires page components to URL paths AND wraps them in the correct
 * layout shells (AppLayout / AppLayoutWithNav).
 *
 * Exports a single `valueMatchingRouteGroups` array that router.tsx spreads in
 * — one spread per domain keeps router.tsx completely thin.
 *
 * To add a new page: add a route to the appropriate inner array below.
 * To add a new layout group: add a new entry to valueMatchingRouteGroups.
 * No changes needed in router.tsx.
 */

import { RouteObject } from 'react-router-dom';
import { ROUTES, VALUE_MATCHING_BASE } from 'src/routes/valueMatching/routes';
import AppLayout from 'src/components/app/AppLayout';
import AppLayoutWithNav from 'src/components/app/AppLayoutWithNav';
import HomePage from 'src/components/pages/valueMatching/HomePage';
import ListCampaignsPage from 'src/components/pages/valueMatching/ListCampaignsPage';
import CreateCampaignPage from 'src/components/pages/valueMatching/CreateCampaignPage';
import ViewCampaignPage from 'src/components/pages/valueMatching/ViewCampaignPage';
import ModifyCampaignPage from 'src/components/pages/valueMatching/ModifyCampaignPage';
import { RouteErrorBoundary } from 'src/components/ErrorBoundary';

/**
 * Complete Value Matching route configuration with layout wrappers.
 * Spread this into the router — one entry per domain.
 *
 * @example
 * ```ts
 * const router = createBrowserRouter([
 *   ...valueMatchingRouteGroups,
 *   // ...promotionsRouteGroups,
 * ]);
 * ```
 */
export const valueMatchingRouteGroups: RouteObject[] = [
  // VM landing page — no side navigation
  {
    element: <AppLayout />,
    children: [
      {
        path: ROUTES.ROOT,
        element: <HomePage />,
        errorElement: <RouteErrorBoundary />,
      },
    ],
  },
  // VM operational pages — with side navigation
  {
    element: <AppLayoutWithNav />,
    children: [
      {
        path: `${VALUE_MATCHING_BASE}/:campaignType/list`,
        element: <ListCampaignsPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: `${VALUE_MATCHING_BASE}/:campaignType/create`,
        element: <CreateCampaignPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: `${VALUE_MATCHING_BASE}/:campaignType/view/:campaignId/:version`,
        element: <ViewCampaignPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: `${VALUE_MATCHING_BASE}/:campaignType/modify/:campaignId/:version`,
        element: <ModifyCampaignPage />,
        errorElement: <RouteErrorBoundary />,
      },
    ],
  },
];
